import './kp-pdf-scrolling-document.less';

import type {PDFPageProxy} from 'pdfjs-dist/types/src/display/api';
import type {IAugmentedJQuery, IDoCheck, IOnDestroy, ITimeoutService} from 'angular';
import type {KpPdfDocumentComponentController} from '../kp-pdf-document/kp-pdf-document.component';
import KpPdfDocumentComponent from '../kp-pdf-document/kp-pdf-document.component';
import {ngComponentCssClass} from 'shared/utils/ng-@decorators';
import type {Component, OnChanges, OnChangesObject} from 'typings/portaro.fe.types';

export abstract class KpPdfScrollingDocumentComponentController implements KpPdfScrollingDocumentComponentBindings, OnChanges<KpPdfScrollingDocumentComponentBindings>, IDoCheck, IOnDestroy {
    public pages: PDFPageProxy[];
    public scrollTop: number;
    public clientHeight: number;
    public enablePageJump = false;
    public currentPage = 1;
    public isParentVisible = true;

    public abstract onPagesReset();

    public abstract onPagesFetch(locals: { $currentPage: number });

    public abstract onPageJump(locals: { $scrollTop: number });

    public pdfDocumentController: KpPdfDocumentComponentController;
    private scrollHandler: () => void;
    private focusedPage: number = undefined;
    private pagesLengthOld = 0;

    /*@ngInject*/
    constructor(private $element: IAugmentedJQuery, private $timeout: ITimeoutService) {
    }

    public $onChanges(onChangesObj: OnChangesObject<KpPdfScrollingDocumentComponentBindings>): void {
        if (onChangesObj.isParentVisible && typeof onChangesObj.isParentVisible.currentValue === 'boolean') {
            this.pdfDocumentController.updateScrollBounds();
        }

        if (onChangesObj.currentPage && onChangesObj.currentPage.currentValue) {
            const currentPage = onChangesObj.currentPage.currentValue;

            if (currentPage > this.pages.length) {
                this.fetchPages(currentPage);
            } else {
                this.focusedPage = currentPage;
            }
        }
    }

    public $doCheck(): void {
        if (this.pagesLength !== this.pagesLengthOld) {
            if (this.pagesLengthOld === 0) {
                this.onPagesReset();
            }

            // Set focusedPage after new pages are mounted
            this.$timeout(() => {
                this.focusedPage = this.currentPage;
            });

            this.pagesLengthOld = this.pagesLength;
        }
    }

    public $onDestroy(): void {
        this.$element[0].removeEventListener('scroll', this.scrollHandler);
    }

    public fetchPages(currentPage: number) {
        this.onPagesFetch({$currentPage: currentPage});
    }

    private get pagesLength() {
        return this.pages.length;
    }
}

export class KpPdfScrollingDocumentComponent implements Component<KpPdfScrollingDocumentComponentBindings> {
    public static componentName = 'kpPdfScrollingDocument';

    @ngComponentCssClass('pdf-scrolling-document')
    public templateUrl = require('./kp-pdf-scrolling-document.tpl.pug');

    public transclude = true;

    public bindings = {
        pages: '<',
        enablePageJump: '<',
        currentPage: '<',
        isParentVisible: '<',
        scrollTop: '<',
        clientHeight: '<',
        onPagesReset: '&?',
        onPagesFetch: '&?',
        onPageJump: '&?'
    } as const;

    public controller = KpPdfScrollingDocumentComponentController;
    public controllerAs = 'pdfScrollingDocumentController';

    public require = {
        pdfDocumentController: `^${KpPdfDocumentComponent.componentName}`
    };
}

interface KpPdfScrollingDocumentComponentBindings {
    pages: PDFPageProxy[];
    scrollTop: number;
    clientHeight: number;
    enablePageJump?: boolean;
    currentPage?: number;
    isParentVisible?: boolean;

    onPagesReset();

    onPagesFetch(locals: { $currentPage: number });

    onPageJump(locals: { $scrollTop: number });
}
